import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormControlLabel, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/redux';
import { FormInputRadio } from '../../../inputs/FormInputRadio';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { ButtonClassNames, ModalVariants } from '../../../../models/Common';
import { ITerminationStep, ITerminateForm, IRefundType } from '../../../../models/ContractTermination';
import { formatDateAsISO, getErrorMessage } from '../../../../utils';

interface TerminateRefundProps {
  formState: ITerminateForm;
  setFormState: React.Dispatch<React.SetStateAction<ITerminateForm>>;
  changeTerminateStep: (step: ITerminationStep) => void;
}

export const TerminateRefund: FC<TerminateRefundProps> = ({ formState, setFormState, changeTerminateStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [createUserDocument, { isLoading }] = userInfoApi.useCreateUserDocumentMutation();
  const methods = useForm<{ refundType: IRefundType }>({
    defaultValues: {
      refundType: formState.refundType,
    },
  });
  const { handleSubmit, watch } = methods;

  const handleProceed = async () => {
    if (watch().refundType === 'cashless' || watch().refundType === 'transfer') {
      setFormState((prevState) => {
        return {
          ...prevState,
          refundType: watch().refundType,
        };
      });
      changeTerminateStep(watch().refundType === 'cashless' ? 'details' : 'transfer');
      return;
    }
    try {
      const docType = watch().refundType === 'cash' ? 'cash' : 'withoutRefund';
      const toDate = formatDateAsISO(
        Number(formState.terminationDate?.day),
        Number(formState.terminationDate?.month),
        Number(formState.terminationDate?.year),
      );
      const terminationPhone = `+38${formState.phone}`;
      const body = { toDate, terminationPhone };
      await createUserDocument({ docType: watch().refundType === 'later' ? 'withoutType' : docType, body }).unwrap();
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('terminateContract.applicationSent')}`,
            description: `${t('terminateContract.supportManagerWillContactYou')}`,
          },
        }),
      );
      changeTerminateStep(undefined);
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleAbortProcess = () => {
    setFormState((prevState) => {
      return {
        ...prevState,
        refundType: undefined,
      };
    });
    changeTerminateStep('run');
  };

  return (
    <FormProvider {...methods}>
      <article className="account__terminate__title">{t('terminateContract.refundType')}</article>
      <form className="account__terminate__refund" onSubmit={handleSubmit(handleProceed)}>
        <FormInputRadio
          name="refundType"
          options={
            <div className="account__terminate__refund__options">
              <div className="account__terminate__refund__option">
                <FormControlLabel
                  value="cash"
                  control={<Radio className="input input__radio" />}
                  label={t('terminateContract.refundCash')}
                />
                {watch().refundType === 'cash' && (
                  <p className="account__terminate__refund__option__help">{t('terminateContract.refundCashHelp')}</p>
                )}
              </div>
              <div className="account__terminate__refund__option">
                <FormControlLabel
                  value="cashless"
                  control={<Radio className="input input__radio" />}
                  label={t('terminateContract.refundCashless')}
                />
              </div>
              <div className="account__terminate__refund__option">
                <FormControlLabel
                  value="transfer"
                  control={<Radio className="input input__radio" />}
                  label={t('terminateContract.refundTransfer')}
                />
              </div>
              <div className="account__terminate__refund__option">
                <FormControlLabel
                  value="none"
                  control={<Radio className="input input__radio" />}
                  label={t('terminateContract.refundNone')}
                />
              </div>
              <div className="account__terminate__refund__option">
                <FormControlLabel
                  value="later"
                  control={<Radio className="input input__radio" />}
                  label={t('terminateContract.refundLater')}
                />
              </div>
            </div>
          }
        />
        <div className="modals__btns-wrapper">
          <div className="btn-group">
            <CustomMUIButton
              label={t('common.back')}
              type="button"
              className={ButtonClassNames.PrimaryFulfilled}
              handleClick={handleAbortProcess}
              isBold
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={
                watch().refundType === 'cashless' || watch().refundType === 'transfer'
                  ? t('buttons.continue')
                  : t('terminateContract.sendApplication')
              }
              type="submit"
              className={ButtonClassNames.SecondaryOutlined}
              isDisabled={isLoading || !watch().refundType}
              isBold
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
